import { EqipApplicationResultsParams, RDEqipApplicationResults } from '@cibo/core'
import { ResourceDetailFeatureTaskEditorProps, Section, useSaveIndicator } from '@cibo/ui'
import { Collapse, Stack } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useProgramEngagement,
  useProgramEngagementDetails,
  useProgramModel,
  useUploadEqipApplicationResults,
} from '../../../../queries'
import { ScheduleOfOperationsUpload } from './ScheduleOfOperationsUpload'
import { YesNoQuestion } from './YesNoQuestion'

/** @deprecated This progressive disclosure question should hereforth be done via activation tags in trait configs */
export const RDEqipApplicationResultsTaskEditor = ({
  detailRequirements,
  resourceIds: [resourceId],
}: ResourceDetailFeatureTaskEditorProps<RDEqipApplicationResults>) => {
  const { t } = useTranslation('@cibo/programs/RDEqipApplicationResultsTaskEditor')
  const { data: details } = useProgramEngagementDetails({
    engagementId: resourceId,
    detailRequirements,
  })
  const { setIsDirty, setIsError, setIsSaving } = useSaveIndicator()
  const uploadEqipApplicationResults = useUploadEqipApplicationResults()
  const engagement = useProgramEngagement({ id: resourceId })
  const programModel = useProgramModel({ programId: engagement.data?.programId })
  const benchmark = programModel.data?.benchmarks?.find(
    ({ benchmark }) => benchmark === 'applicationResults'
  )
  const existingDetail: RDEqipApplicationResults | Record<any, any> = useMemo(
    () => (!!details?.length ? details[0] : {}),
    [details]
  )
  const locked = existingDetail?.immutable
  const existingResult = existingDetail.result || {}

  const hasFile = !!existingResult?.file?.length
  const displayFileInput = existingResult?.applicationApproved || hasFile

  const onChange = (args: Omit<EqipApplicationResultsParams, 'id'>) => {
    setIsError(false)
    setIsDirty(true)
    setIsSaving(true)
    const { applicationApproved, totalContractValue } = existingResult
    uploadEqipApplicationResults
      .mutateAsync({
        id: resourceId,
        year: benchmark && benchmark.details[0].year,
        responseReceived: true,
        applicationApproved,
        totalContractValue,
        ...args,
      })
      .catch(() => setIsError(true))
      .finally(() => setIsSaving(false))
  }

  return (
    <Stack spacing={3} flex={1}>
      <Section
        sx={{
          flex: 1,
          marginBottom: displayFileInput ? undefined : -3,
          transition: 'margin-bottom 0.2s ease-in-out',
        }}
      >
        <Stack padding={3} spacing={3}>
          <YesNoQuestion
            data-testid="applicationApproved"
            disabled={locked || uploadEqipApplicationResults.isPending || hasFile}
            onChange={answer => onChange({ applicationApproved: answer === 'true' })}
            noLabel={t('applicationNotApproved')}
            yesLabel={t('applicationApproved')}
            questionLabel={t('wereYouApproved')}
            isError={uploadEqipApplicationResults.isError}
            value={existingResult?.applicationApproved}
          />

          <Collapse in={existingResult?.applicationApproved === false}>
            <YesNoQuestion
              data-testid="willReapply"
              disabled={locked || uploadEqipApplicationResults.isPending || hasFile}
              onChange={answer => onChange({ willReapply: answer === 'true' })}
              noLabel={t('no')}
              yesLabel={t('yes')}
              questionLabel={t('wouldYouReapply', { nextApplicationYear: '2026' })}
              isError={uploadEqipApplicationResults.isError}
              value={existingResult?.willReapply}
            />
          </Collapse>
        </Stack>
      </Section>

      <Collapse in={displayFileInput} timeout={0.2}>
        <Section sx={{ flex: 1 }}>
          <Stack padding={3} spacing={3}>
            <ScheduleOfOperationsUpload
              currentFile={existingResult?.file}
              currentDetail={{
                ...existingResult,
                year: benchmark && benchmark.details[0].year,
              }}
              engagementId={resourceId}
              disabled={locked}
            />
          </Stack>
        </Section>
      </Collapse>
    </Stack>
  )
}
